import type { PartnerAppConfig } from "@expert/app-config/types";
import type { Partner } from "@expert/shared-types";

export const isProd = import.meta.env.VITE_ENV_TYPE === "production" && import.meta.env.PROD;

function createFeaturesStub({
    callbackLater = false,
    outboundCall = false,
    surveyEnabled = false,
    medialess = false,
    verificationType = "phoneNumber",
} = {}) {
    return {
        callbackLater,
        outboundCall,
        surveyEnabled,
        medialess,
        verificationType,
    };
}

function createPartnerConfigStub({
    features = createFeaturesStub(),
    activationSupportNumbers = [],
    programs = [],
    requiresManualCopy = false,
} = {}) {
    return {
        features,
        activationSupportNumbers,
        programs,
        requiresManualCopy,
    };
}

const configStub = {
    asurion: createPartnerConfigStub(),
    cricket: createPartnerConfigStub(),
    cricketprotecthome: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    verizon: createPartnerConfigStub({
        features: createFeaturesStub({
            callbackLater: true,
            outboundCall: true,
        }),
    }),
    att: createPartnerConfigStub(),
    tmobile: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    aafes: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    adt: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    adtbdpp: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    amazonrouter: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    mcafeesecurityscan: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    asurionphonecare: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    asurionhomeplus: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    bluestream: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    brightspeed: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    cactus: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    cox: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    dish: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    ee: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    frontier: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    hotwire: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    comcastbusiness: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    hpsales: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    koodo: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    leakprotect: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    liberty: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    mcafeetechsupport: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    mediacom: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    others: createPartnerConfigStub(),
    nexcom: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    samsungna: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    sparklight: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    sparklightsales: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    staples: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    stapleshome: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    staplessmb: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    telus: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    telushomepro: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    thd: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    thesource: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    uscellular: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    viasat: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    fios: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    optimum: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    microsoft: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    life360: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    reach: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    tracfone: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    dell: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    pilot: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    nrg: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    smartpay: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    asuriontechcare: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    attconnectedhome: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    attnac: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    optimumbiz: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    verizonconnectedhome: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    verizonwholeoffice: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    vzwsmb: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    wizperk: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    xboxcareplus: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    yahoo: createPartnerConfigStub({
        features: createFeaturesStub(),
    }),
    unknown: createPartnerConfigStub(),
};

const setupPartnersConfigStub = () => (partner: Partner) => {
    return (configStub[partner as keyof typeof configStub] ?? configStub.unknown) as unknown as PartnerAppConfig;
};

export const appConfigStub = {
    getPartnersConfig: setupPartnersConfigStub(),
};
